import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BookingRow, LifecycleStatus } from '../types';
import { LifecyclePill, PaymentPill } from './StatusPill';
import { CheckinButton } from './CheckinButton';
import { formatVenueTime } from '../lib/time';
import { bookingDisplayName } from '../lib/guest-name';
import { useIsSingleRoomByRoomId } from '../lib/room-counts';
import { enqueueDeparture } from '../api/queue';
import type { QueueApiError } from '../api/queue';

interface Props {
  booking: BookingRow;
  onLifecycleChange?: (id: string, next: LifecycleStatus) => void;
  /** Fires after a successful re-enqueue (only relevant for arrived rows
   *  that aren't in the queue). Parent should refetch the queue. */
  onRequeued?: () => void;
  showDate?: boolean;
}

export function BookingCard({ booking, onLifecycleChange, onRequeued, showDate = false }: Props) {
  const [requeueBusy, setRequeueBusy] = useState(false);
  const [requeueError, setRequeueError] = useState<string | null>(null);

  async function handleRequeue() {
    if (requeueBusy) return;
    setRequeueBusy(true);
    setRequeueError(null);
    try {
      await enqueueDeparture(booking.id);
      onRequeued?.();
    } catch (e) {
      const err = e as Partial<QueueApiError>;
      // already_queued / not_arrived / queue_location_unresolved all
      // surface here. Show a one-line hint; the operator can re-check the
      // pending list which will refresh on the next poll anyway.
      setRequeueError(err.message ?? String(e));
    } finally {
      setRequeueBusy(false);
    }
  }
  const slot = booking.booking_slots[0];
  const customer = booking.customers;
  const fullName = bookingDisplayName(booking) || 'Geen klantgegevens';
  const isSingleRoom = useIsSingleRoomByRoomId();
  const showRoom = slot ? !isSingleRoom(slot.room_id) : false;

  return (
    <div className="booking-card">
      <div className="booking-card-time">
        {slot ? (
          <>
            <span className="booking-time">{formatVenueTime(slot.slot_start)}</span>
            {showDate ? (
              <span className="booking-date">
                {new Date(slot.slot_start).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'short',
                })}
              </span>
            ) : null}
            {showRoom ? (
              <span className="booking-room">{slot.rooms?.name ?? '—'}</span>
            ) : null}
          </>
        ) : (
          <span className="booking-time">—</span>
        )}
      </div>

      <div className="booking-card-main">
        <Link to={`/booking/${booking.booking_number}`} className="booking-card-link">
          <div className="booking-headline">
            <strong>{booking.booking_number}</strong>
            <span className="muted">·</span>
            <span>{fullName}</span>
            <span className="muted">·</span>
            <span>{booking.party_size}p</span>
          </div>
          <div className="booking-sub">
            <span>{booking.experiences?.name ?? '—'}</span>
            {customer?.email ? <span className="muted">{customer.email}</span> : null}
          </div>
        </Link>
        <div className="booking-pills">
          <LifecyclePill status={booking.lifecycle_status} />
          <PaymentPill status={booking.payment_status} />
        </div>
      </div>

      <div className="booking-card-action">
        <CheckinButton
          booking={booking}
          onChanged={(next) => onLifecycleChange?.(booking.id, next)}
        />
        {/* Pending-list rows hide booking-domain entries that are already in
            the queue (TodayScreen filters them), so any 'arrived' row that
            shows up here is one the operator removed (or never enqueued).
            Offer a one-click re-add. */}
        {booking.lifecycle_status === 'arrived' ? (
          <>
            <button
              type="button"
              className="btn-secondary booking-card-requeue"
              disabled={requeueBusy}
              onClick={() => void handleRequeue()}
              title="Voeg deze (al ingecheckte) klant terug in de wachtrij."
            >
              {requeueBusy ? '…' : 'Voeg toe aan wachtrij'}
            </button>
            {requeueError ? (
              <span className="booking-card-requeue-error" role="status">
                {requeueError}
              </span>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
